import {Box, Typography, Breadcrumbs, Link, Button} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {Link as RouterLink} from 'react-router-dom';
import {ReactNode} from 'react';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  /** Small control rendered beside the title (e.g. a preference icon) */
  titleAdornment?: ReactNode;
  /** Right-side content — typically action buttons */
  actions?: ReactNode;
  /** Shows a "← Back" link above the title */
  backLink?: {label: string; href: string};
  /** Breadcrumb trail above the title */
  breadcrumbs?: BreadcrumbItem[];
  /** Content rendered below title (e.g. metadata chips, description) */
  children?: ReactNode;
}

export default function PageHeader({
  title,
  subtitle,
  titleAdornment,
  actions,
  backLink,
  breadcrumbs,
  children,
}: PageHeaderProps) {
  return (
    <Box sx={{mb: 3}}>
      {backLink && (
        <Button
          component={RouterLink}
          to={backLink.href}
          startIcon={<ArrowBackIcon sx={{fontSize: 18}} />}
          size="small"
          variant="outlined"
          sx={{
            mb: 1.5,
            ml: -0.5,
            textTransform: 'none',
            fontWeight: 600,
            color: 'primary.main',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            '&:hover': {
              bgcolor: 'action.hover',
              borderColor: 'primary.main',
            },
          }}
        >
          {backLink.label}
        </Button>
      )}

      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs sx={{mb: 1}}>
          {breadcrumbs.map((crumb, idx) => {
            const isLast = idx === breadcrumbs.length - 1;
            if (isLast || !crumb.href) {
              return (
                <Typography key={idx} variant="body2" color="text.primary" fontWeight={500}>
                  {crumb.label}
                </Typography>
              );
            }
            return (
              <Link
                key={idx}
                component={RouterLink}
                to={crumb.href}
                underline="hover"
                color="text.secondary"
                variant="body2"
              >
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      )}

      <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2}}>
        <Box sx={{minWidth: 0}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0}}>
            <Typography variant="h4" noWrap>
              {title}
            </Typography>
            {titleAdornment}
          </Box>
          {subtitle && (
            <Typography variant="body1" color="text.secondary" sx={{mt: 0.5}}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions && (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0}}>
            {actions}
          </Box>
        )}
      </Box>

      {children && <Box sx={{mt: 1.5}}>{children}</Box>}
    </Box>
  );
}
